import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  useCreateCalendarEvent,
  useDeleteCalendarEvent,
  useUpdateCalendarEvent,
  type CreateCalendarEventInput,
  type UpdateCalendarEventInput,
} from '@fluxter/shared';
import { Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MemberAvatar } from '@/components/MemberAvatar';
import { useConfirm } from '@/components/ConfirmDialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { CalendarEventRow } from '@/components/CalendarView';

/* ─ Types ────────────────────────────────────────────────────────── */

export type DialogMode = 'create' | 'edit';

export interface CalendarEventDialogMember {
  id: string;
  name: string | null;
  email: string | null;
}

export interface CalendarEventDialogProject {
  id: string;
  slug: string;
  name: string;
}

export interface CalendarEventDialogProps {
  open: boolean;
  onClose: () => void;
  workspaceSlug: string;
  mode: DialogMode;
  /** Required when mode = 'edit'. Ignored otherwise. */
  event?: CalendarEventRow & { creatorId?: string; projectId?: string | null };
  /** Workspace members for the attendee picker. */
  members: ReadonlyArray<CalendarEventDialogMember>;
  /** All projects in the workspace. Pass empty list to disable the
   *  project picker (e.g. on read-only previews). */
  projects: ReadonlyArray<CalendarEventDialogProject>;
  /** Pre-fill the project on create (e.g. when on a project calendar
   *  page). Ignored on edit (server returns the current value). */
  defaultProjectId?: string | null;
  /** Pre-fill the start date/time on create (e.g. when triggered by
   *  click-empty-cell — not wired in 13c, reserved for v2). */
  defaultStartAt?: string;
  defaultIsAllDay?: boolean;
}

/* ─ Date helpers ──────────────────────────────────────────────────── */

/** Format a Date for `<input type="datetime-local">` (no seconds, no tz). */
const toDatetimeLocalValue = (d: Date): string => {
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${yy}-${mm}-${dd}T${hh}:${mi}`;
};

/** Format a Date as `YYYY-MM-DD` for `<input type="date">`. */
const toDateValue = (d: Date): string => {
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

/** Parse an ISO 8601 string into local-tz components for input fields. */
const isoToInputValues = (
  iso: string,
  isAllDay: boolean,
): { date: string; datetime: string } => {
  if (!iso) {
    const now = new Date();
    return { date: toDateValue(now), datetime: toDatetimeLocalValue(now) };
  }
  // For all-day, treat as date-only (ignore tz so May 8 stays May 8).
  if (isAllDay) {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (m) {
      return { date: `${m[1]}-${m[2]}-${m[3]}`, datetime: `${m[1]}-${m[2]}-${m[3]}T00:00` };
    }
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return { date: '', datetime: '' };
  }
  return { date: toDateValue(d), datetime: toDatetimeLocalValue(d) };
};

/** Convert input values back to ISO for sending to the API. */
const inputValueToIso = (value: string, isAllDay: boolean): string => {
  if (!value) return '';
  if (isAllDay) {
    // Already YYYY-MM-DD; backend treats as calendar date.
    return value.length === 10 ? value : value.slice(0, 10);
  }
  // datetime-local in user's tz → UTC ISO so backend stores
  // matching wall-clock time. `new Date('2026-05-08T14:30')` is
  // interpreted as local; .toISOString() gives the UTC equivalent.
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
};

/* ─ Dialog ────────────────────────────────────────────────────────── */

export const CalendarEventDialog = ({
  open,
  onClose,
  workspaceSlug,
  mode,
  event,
  members,
  projects,
  defaultProjectId,
  defaultStartAt,
  defaultIsAllDay,
}: CalendarEventDialogProps) => {
  const create = useCreateCalendarEvent(workspaceSlug);
  const update = useUpdateCalendarEvent(workspaceSlug, event?.id ?? '');
  const remove = useDeleteCalendarEvent(workspaceSlug);
  const { confirm, dialog: confirmDialogEl } = useConfirm();

  /* ── form state ── */
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [isAllDay, setIsAllDay] = useState(false);
  const [startValue, setStartValue] = useState('');
  const [endValue, setEndValue] = useState('');
  const [projectId, setProjectId] = useState<string>('');
  const [attendeeIds, setAttendeeIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // (Re)hydrate form when dialog opens or its target event changes.
  // `event?.id` covers edit mode; `mode` + `defaultStartAt` cover
  // create-mode prefills.
  useEffect(() => {
    if (!open) return;

    if (mode === 'edit' && event) {
      setTitle(event.title);
      setDescription('');
      setLocation(event.location ?? '');
      setIsAllDay(event.isAllDay);
      const startVals = isoToInputValues(event.startAt, event.isAllDay);
      setStartValue(event.isAllDay ? startVals.date : startVals.datetime);
      if (event.endAt) {
        const endVals = isoToInputValues(event.endAt, event.isAllDay);
        setEndValue(event.isAllDay ? endVals.date : endVals.datetime);
      } else {
        setEndValue('');
      }
      setProjectId(event.projectId ?? '');
      setAttendeeIds(event.attendees.map((a) => a.userId));
      setError(null);
      return;
    }

    // create mode
    const startSeed = defaultStartAt
      ? isoToInputValues(defaultStartAt, defaultIsAllDay ?? false)
      : (() => {
          // Default: tomorrow at 10am (timed) OR tomorrow (all-day).
          const t = new Date();
          t.setDate(t.getDate() + 1);
          t.setSeconds(0, 0);
          if (defaultIsAllDay) {
            t.setHours(0, 0, 0, 0);
          } else {
            t.setHours(10, 0, 0, 0);
          }
          return {
            date: toDateValue(t),
            datetime: toDatetimeLocalValue(t),
          };
        })();
    const isAD = defaultIsAllDay ?? false;
    setTitle('');
    setDescription('');
    setLocation('');
    setIsAllDay(isAD);
    setStartValue(isAD ? startSeed.date : startSeed.datetime);
    if (isAD) {
      setEndValue('');
    } else {
      // Default end = start + 1 hour for timed events.
      const startD = new Date(startSeed.datetime);
      startD.setHours(startD.getHours() + 1);
      setEndValue(toDatetimeLocalValue(startD));
    }
    setProjectId(defaultProjectId ?? '');
    setAttendeeIds([]);
    setError(null);
  }, [open, mode, event, defaultProjectId, defaultStartAt, defaultIsAllDay]);

  // When toggling all-day ↔ timed, reformat the start/end so the
  // input controls receive the right value shape (date vs datetime-
  // local). Don't fire on initial open (the effect above handles it).
  const toggleAllDay = (next: boolean): void => {
    setIsAllDay(next);
    // Reformat start.
    if (startValue) {
      if (next) {
        // datetime → date: drop time portion.
        setStartValue(startValue.slice(0, 10));
      } else {
        // date → datetime: append default time (10:00).
        setStartValue(`${startValue}T10:00`);
      }
    }
    // Reformat end. When toggling timed → all-day, drop end if it's
    // the same calendar date as start (single-day all-day = end null).
    // When toggling all-day → timed, default end = start + 1 hour.
    if (next) {
      if (endValue) {
        setEndValue(endValue.slice(0, 10));
      }
    } else {
      const start = new Date(startValue.slice(0, 10) + 'T10:00');
      start.setHours(start.getHours() + 1);
      setEndValue(toDatetimeLocalValue(start));
    }
  };

  /* ── attendee helpers ── */
  const memberById = useMemo(
    () => new Map(members.map((m) => [m.id, m])),
    [members],
  );
  const creatorId = event?.creatorId ?? null;
  const isCreatorChip = (id: string): boolean => mode === 'edit' && id === creatorId;

  const toggleAttendee = (id: string): void => {
    setAttendeeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  /* ── submit / delete ── */
  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);

    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    const startIso = inputValueToIso(startValue, isAllDay);
    if (!startIso) {
      setError('Start date is required.');
      return;
    }
    const endIso =
      endValue && (!isAllDay || endValue !== startValue)
        ? inputValueToIso(endValue, isAllDay)
        : null;
    if (!isAllDay && !endIso) {
      setError('End time is required for timed events.');
      return;
    }
    if (endIso && new Date(endIso).getTime() < new Date(startIso).getTime()) {
      setError('End time must be on or after the start time.');
      return;
    }

    try {
      if (mode === 'edit' && event) {
        const payload: UpdateCalendarEventInput = {
          title: title.trim(),
          description: description.trim() ? description.trim() : null,
          location: location.trim() ? location.trim() : null,
          start_at: startIso,
          end_at: endIso,
          is_all_day: isAllDay,
          project_id: projectId || null,
          attendee_ids: attendeeIds,
        };
        await update.mutateAsync(payload);
        toast.success('Event updated.');
      } else {
        const payload: CreateCalendarEventInput = {
          title: title.trim(),
          description: description.trim() ? description.trim() : null,
          location: location.trim() ? location.trim() : null,
          start_at: startIso,
          end_at: endIso,
          is_all_day: isAllDay,
          project_id: projectId || null,
          attendee_ids: attendeeIds,
        };
        await create.mutateAsync(payload);
        toast.success('Event created.');
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save event.');
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (mode !== 'edit' || !event) return;
    const ok = await confirm({
      title: 'Delete this event?',
      description: 'This removes the event for every attendee. This cannot be undone.',
      confirmLabel: 'Delete event',
      destructive: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(event.id);
      toast.success('Event deleted.');
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete event.');
    }
  };

  const pending = create.isPending || update.isPending || remove.isPending;
  const titleLabel = mode === 'edit' ? 'Edit event' : 'New event';
  const submitLabel = pending
    ? 'Saving…'
    : mode === 'edit'
      ? 'Save changes'
      : 'Create event';

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{titleLabel}</DialogTitle>
            <DialogDescription>
              {mode === 'edit'
                ? 'Updates fan out to every attendee. Times are stored in UTC and rendered in your local timezone.'
                : 'Schedule a meeting, focus block, or deadline. Optionally attach to a project so the event also shows in that project\'s calendar.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="event-title">Title</Label>
              <Input
                id="event-title"
                type="text"
                required
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Sprint planning"
              />
            </div>

            {/* All-day toggle — plain HTML checkbox styled inline; no
                shadcn checkbox primitive in the project today and one
                box doesn't justify adding one. */}
            <div className="flex items-center gap-2">
              <input
                id="event-all-day"
                type="checkbox"
                checked={isAllDay}
                onChange={(e) => toggleAllDay(e.target.checked)}
                className="size-4 rounded border-input accent-[var(--brand-accent)]"
              />
              <Label htmlFor="event-all-day" className="cursor-pointer text-[12.5px]">
                All-day event
              </Label>
            </div>

            {/* Start + end */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="event-start">Start</Label>
                <Input
                  id="event-start"
                  type={isAllDay ? 'date' : 'datetime-local'}
                  required
                  value={startValue}
                  onChange={(e) => setStartValue(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="event-end">
                  End {isAllDay ? '(optional)' : ''}
                </Label>
                <Input
                  id="event-end"
                  type={isAllDay ? 'date' : 'datetime-local'}
                  required={!isAllDay}
                  value={endValue}
                  onChange={(e) => setEndValue(e.target.value)}
                />
              </div>
            </div>

            {/* Project */}
            {projects.length > 0 ? (
              <div className="space-y-2">
                <Label htmlFor="event-project">Project (optional)</Label>
                <Select
                  value={projectId || '__none__'}
                  onValueChange={(v) => setProjectId(v === '__none__' ? '' : v)}
                >
                  <SelectTrigger id="event-project">
                    <SelectValue placeholder="Workspace-only (no project)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No project</SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {/* Location */}
            <div className="space-y-2">
              <Label htmlFor="event-location">Location (optional)</Label>
              <Input
                id="event-location"
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="https://meet.google.com/abc-defg-hij"
              />
            </div>

            {/* Attendees */}
            <div className="space-y-2">
              <Label>Attendees</Label>
              <AttendeeMultiSelect
                members={members}
                attendeeIds={attendeeIds}
                onToggle={toggleAttendee}
                isCreatorChip={isCreatorChip}
              />
              <p className="text-foreground-faint text-[11px]">
                {mode === 'create'
                  ? 'You\'re the creator and will be added automatically.'
                  : 'The creator can\'t be removed here — delete the event instead to disband.'}
              </p>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="event-description">Description (optional)</Label>
              <Textarea
                id="event-description"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Agenda, notes, links…"
              />
            </div>

            {error ? (
              <p className="text-destructive text-sm" role="alert">
                {error}
              </p>
            ) : null}

            <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-between">
              {mode === 'edit' ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void handleDelete()}
                  disabled={pending}
                  className="border-destructive/40 text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="size-3.5" />
                  Delete
                </Button>
              ) : <span />}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={onClose} disabled={pending}>
                  Cancel
                </Button>
                <Button type="submit" disabled={pending || !title.trim()}>
                  {submitLabel}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {confirmDialogEl}
    </>
  );
};

/* ─ Attendee multi-select ─────────────────────────────────────────── */

interface AttendeeMultiSelectProps {
  members: ReadonlyArray<CalendarEventDialogMember>;
  attendeeIds: string[];
  onToggle: (userId: string) => void;
  isCreatorChip: (id: string) => boolean;
}

const AttendeeMultiSelect = ({
  members,
  attendeeIds,
  onToggle,
  isCreatorChip,
}: AttendeeMultiSelectProps) => {
  const [open, setOpen] = useState(false);
  const memberById = useMemo(
    () => new Map(members.map((m) => [m.id, m])),
    [members],
  );

  return (
    <div className="space-y-2">
      {/* Selected attendees as chips. Creator chip is greyed + non-
          removable per Q12. */}
      <div className="flex flex-wrap items-center gap-1.5 rounded-lg border border-input bg-background px-2 py-1.5 min-h-[34px]">
        {attendeeIds.length === 0 ? (
          <span className="text-foreground-faint text-[11.5px]">
            No attendees yet
          </span>
        ) : (
          attendeeIds.map((id) => {
            const m = memberById.get(id);
            const label = m?.name ?? m?.email ?? 'Unknown';
            const creator = isCreatorChip(id);
            return (
              <span
                key={id}
                className={cn(
                  'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11.5px]',
                  creator
                    ? 'border-foreground-faint/30 bg-secondary text-foreground-muted'
                    : 'border-[var(--brand-accent)]/30 bg-[oklch(0.74_0.16_50/0.10)] text-foreground',
                )}
              >
                <MemberAvatar
                  id={id}
                  name={m?.name ?? null}
                  email={m?.email ?? null}
                  size="xs"
                  className="!h-4 !w-4 !text-[7px]"
                />
                <span className="max-w-[120px] truncate">{label}</span>
                {creator ? (
                  <span className="text-foreground-faint text-[10px]">(creator)</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onToggle(id)}
                    className="text-foreground-muted hover:text-foreground inline-flex items-center"
                    aria-label={`Remove ${label}`}
                  >
                    <X className="size-3" />
                  </button>
                )}
              </span>
            );
          })
        )}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                'text-foreground-muted hover:text-foreground inline-flex h-5 items-center rounded-md border border-dashed px-1.5 text-[11px]',
                'border-foreground-faint/30',
              )}
            >
              + Add
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="p-0 w-[280px]">
            <Command>
              <CommandInput placeholder="Search workspace members…" />
              <CommandList>
                <CommandEmpty>No members match.</CommandEmpty>
                <CommandGroup>
                  {members.map((m) => {
                    const checked = attendeeIds.includes(m.id);
                    const label = m.name ?? m.email ?? 'Unknown';
                    return (
                      <CommandItem
                        key={m.id}
                        value={`${m.name ?? ''} ${m.email ?? ''} ${m.id}`}
                        onSelect={() => onToggle(m.id)}
                      >
                        <span
                          aria-hidden="true"
                          className="text-foreground-muted inline-flex w-3.5 justify-center text-[12px]"
                        >
                          {checked ? '✓' : ''}
                        </span>
                        <MemberAvatar
                          id={m.id}
                          name={m.name}
                          email={m.email}
                          size="xs"
                          className="!h-4 !w-4 !text-[7px]"
                        />
                        <span className="truncate">{label}</span>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
